import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRisingEdgeEffect } from 'src/clientLib/hooks'
import { ubemIoApi } from 'src/redux/services/ubem'
import { AsyncTask } from 'src/redux/slices/async-processing/consts'
import { startAsyncTask } from 'src/redux/slices/async-processing/reducers'
import {
  useNavigationEffect,
  useTabNavigation
} from 'src/redux/slices/navigation/hooks'
import { getProject, uploadGISFile } from 'src/redux/slices/project/endpoints'
import { isLoading } from 'src/redux/slices/project/selectors'
import { Entrypoint } from 'src/redux/slices/umibuilder/consts'
import {
  clearAllData,
  loadGeoJSON,
  setCloudContext,
  setEntrypoint
} from 'src/redux/slices/umibuilder/reducers'
import { selectUmiBuilderFromCloud } from 'src/redux/slices/umibuilder/selectors'
import { BaseGisData } from 'src/redux/slices/umibuilder/types'

import {
  Button,
  Grid,
  Icon,
  IconButton,
  LinearProgress,
  Typography
} from '@material-ui/core'
import CacheIcon from '@material-ui/icons/Cached'
import CancelIcon from '@material-ui/icons/Cancel'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import StorageIcon from '@material-ui/icons/Storage'

import GISDropzone from './DropzoneGIS'

///// Component
const GISPanel: React.FC = () => {
  const dispatch = useDispatch()
  // TODO: This is used as a trigger to navigate after first processing round, not great.
  const dataLoadedIntoUmibuilderFromCloud = useSelector(selectUmiBuilderFromCloud)
  const [waitingToFinish, setIsWaitingToFinish] = useState(false) // Gates the navigation effects
  const { data: project, isLoading: isLoadingProject } = getProject.useQuery('')
  const { data: gisFileDoneUploading } = uploadGISFile.useMutation({
    fixedCacheKey: 'uploadGISFile',
  })[1]
  const loading = useSelector(isLoading)

  const [showDropzone, setShowDropZone] = useState(false)

  const toggleDropzone = useCallback(() => {
    setShowDropZone((prev) => !prev)
  }, [setShowDropZone])

  const processFiles = useCallback(() => {
    if (!project) return
    setIsWaitingToFinish(true)
    const { id: project_id } = project

    const body = {
      fromCloud: true,
      project_id,
    }
    dispatch(
      startAsyncTask({
        endpoint: AsyncTask.processBaseGis,
        body,
      }),
    )
  }, [dispatch, project, setIsWaitingToFinish])

  useRisingEdgeEffect({
    callback: processFiles,
    condition: gisFileDoneUploading,
  })
  const { goToTab } = useTabNavigation()

  const goToEditKeyFields = useCallback(() => {
    if (!project) return
    // TODO: these dispatches become unnecessary if we migrate to 100% online
    dispatch(clearAllData())
    // TODO: should be conditional based off of user role
    dispatch(setEntrypoint({ entrypoint: Entrypoint.gis }))
    dispatch(setCloudContext(true))
    const { geoJSON, largeGeoJSON } = project
    const typeSafeGeoJSON = geoJSON as BaseGisData['geoJSON']
    if (!typeSafeGeoJSON) return
    dispatch(loadGeoJSON({ geoJSON: typeSafeGeoJSON, largeGeoJSON: largeGeoJSON ?? false }))
    goToTab('../umibuilder/keyfields')
    setIsWaitingToFinish(true)
  }, [goToTab, dispatch, project, setIsWaitingToFinish])

  useRisingEdgeEffect({
    condition: dataLoadedIntoUmibuilderFromCloud,
    callback: () => dispatch(ubemIoApi.util.invalidateTags(['Scenarios', 'Active Project'])),
  })

  useNavigationEffect({
    condition: dataLoadedIntoUmibuilderFromCloud && waitingToFinish,
    destination: '../umibuilder/keyfields',
  })

  // TODO: convert ISO3 code to counry name
  // if (project) {
  //     const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  //     const name = regionNames.of("PRT"); // "United States"
  //     console.log("name", name);
  // }

  if (isLoadingProject || !project)
    return (
      <Grid container>
        <Grid item xs={2}>
          <LinearProgress />
        </Grid>
      </Grid>
    )
  return (
    <Grid container alignItems='center' justifyContent='flex-start' spacing={2}>
      {project.gis_file_location && !showDropzone && (
        <>
          <Grid item>
            <Typography variant='overline'>GIS Status: </Typography>
          </Grid>
          <Grid>
            <Typography variant='body2'>
              File Uploaded{' '}
              <Typography variant='overline'>
                (v
                {String(project.gis_file_version).padStart(2, '0')})
              </Typography>
            </Typography>
          </Grid>
          <Grid item>
            <Icon color='primary'>
              <CheckCircleIcon />
            </Icon>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={toggleDropzone}
              disabled={loading}
              endIcon={<CacheIcon />}
            >
              Replace
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={goToEditKeyFields}
              disabled={loading}
              endIcon={<StorageIcon />}
            >
              Change Key Fields
            </Button>
          </Grid>
        </>
      )}
      {(!project.gis_file_location || showDropzone) && (
        <>
          <Grid item xs={3}>
            <GISDropzone toggleDropzone={toggleDropzone} />
          </Grid>
          {project.gis_file_location && (
            <Grid item>
              <IconButton onClick={toggleDropzone} color='primary' disabled={loading}>
                <CancelIcon />
              </IconButton>
            </Grid>
          )}
        </>
      )}
    </Grid>
  )
}

export default GISPanel
